import { c as _c } from "react/compiler-runtime";
import { IconAlertTriangle, IconHelp } from "@tabler/icons-react";
import { ReportSection } from "@/components/domain/report-section";
import { MetaTagsGrid } from "@/components/domain/seo/meta-tags-grid";
import { RedirectedAlert } from "@/components/domain/seo/redirected-alert";
import { RobotsSummary } from "@/components/domain/seo/robots-summary";
import { SocialPreviews } from "@/components/domain/seo/social-previews";
import { sections } from "@/lib/constants/sections";
import type { SeoResponse } from "@domainstack/types";
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "@domainstack/ui/empty";
function resolveTwitterVariant(data?: SeoResponse | null): "compact" | "large" {
  const twitterCard = data?.meta?.twitter?.card?.toLowerCase();
  if (twitterCard === "summary_large_image") return "large";
  if (twitterCard === "summary") return "compact";
  return data?.preview?.image ? "large" : "compact";
}
function SeoEmpty(t0) {
  const $ = _c(10);
  const {
    icon: Icon,
    title,
    description
  } = t0;
  let t1;
  if ($[0] !== Icon) {
    t1 = <EmptyMedia variant="icon"><Icon /></EmptyMedia>;
    $[0] = Icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== title) {
    t2 = <EmptyTitle>{title}</EmptyTitle>;
    $[2] = title;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== description) {
    t3 = <EmptyDescription>{description}</EmptyDescription>;
    $[4] = description;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t4 = <Empty className="border border-solid bg-background/60"><EmptyHeader>{t1}{t2}{t3}</EmptyHeader></Empty>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
function SeoMetaBlock(t0) {
  const $ = _c(13);
  const {
    domain,
    data,
    metaTagValues,
    hasHtmlError,
    hasAnySeoMeta
  } = t0;
  if (hasHtmlError) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <SeoEmpty icon={IconAlertTriangle} title="Couldn't fetch page meta" description="We weren't able to retrieve the HTML for this page to extract its meta tags." />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (!hasAnySeoMeta) {
    let t1;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <SeoEmpty icon={IconHelp} title="No SEO meta detected" description="We didn't find standard SEO meta tags (title, description, canonical, or open graph). Add them to improve link previews." />;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  const t1 = data?.source?.finalUrl ?? undefined;
  let t2;
  if ($[2] !== domain || $[3] !== t1) {
    t2 = <RedirectedAlert domain={domain} finalUrl={t1} />;
    $[2] = domain;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== metaTagValues) {
    t3 = <MetaTagsGrid metaTagValues={metaTagValues} />;
    $[5] = metaTagValues;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== data) {
    t4 = data?.preview ? <SocialPreviews preview={data.preview} twitterVariant={resolveTwitterVariant(data)} /> : null;
    $[7] = data;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t2 || $[10] !== t3 || $[11] !== t4) {
    t5 = <>{t2}{t3}{t4}</>;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
}
function RobotsBlock(t0) {
  const $ = _c(4);
  const {
    domain,
    robots,
    hasRobotsData
  } = t0;
  if (hasRobotsData && robots) {
    let t1;
    if ($[0] !== domain || $[1] !== robots) {
      t1 = <RobotsSummary domain={domain} robots={robots} />;
      $[0] = domain;
      $[1] = robots;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <><div className="mt-5 text-[11px] leading-none tracking-[0.08em] text-foreground/70 uppercase dark:text-foreground/80">robots.txt</div><SeoEmpty icon={IconHelp} title="No robots.txt found" description="We didn't find a robots.txt for this site. Crawlers will use default behavior until one is added." /></>;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
export function SeoSection(t0) {
  const $ = _c(40);
  const {
    domain,
    data
  } = t0;
  const t1 = data?.preview?.title;
  let t2;
  if ($[0] !== t1) {
    t2 = {
      label: "Title",
      value: t1
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = data?.preview?.description;
  let t4;
  if ($[2] !== t3) {
    t4 = {
      label: "Description",
      value: t3
    };
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const t5 = data?.meta?.general.keywords;
  let t6;
  if ($[4] !== t5) {
    t6 = {
      label: "Keywords",
      value: t5
    };
    $[4] = t5;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const t7 = data?.meta?.general.author;
  let t8;
  if ($[6] !== t7) {
    t8 = {
      label: "Author",
      value: t7
    };
    $[6] = t7;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  const t9 = data?.preview?.canonicalUrl;
  let t10;
  if ($[8] !== t9) {
    t10 = {
      label: "Canonical",
      value: t9
    };
    $[8] = t9;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  const t11 = data?.preview?.image;
  let t12;
  if ($[10] !== t11) {
    t12 = {
      label: "Image",
      value: t11
    };
    $[10] = t11;
    $[11] = t12;
  } else {
    t12 = $[11];
  }
  const t13 = data?.meta?.general.generator;
  let t14;
  if ($[12] !== t13) {
    t14 = {
      label: "Generator",
      value: t13
    };
    $[12] = t13;
    $[13] = t14;
  } else {
    t14 = $[13];
  }
  const t15 = data?.meta?.general.robots;
  let t16;
  if ($[14] !== t15) {
    t16 = {
      label: "Robots",
      value: t15
    };
    $[14] = t15;
    $[15] = t16;
  } else {
    t16 = $[15];
  }
  let t17;
  if ($[16] !== t10 || $[17] !== t12 || $[18] !== t14 || $[19] !== t16 || $[20] !== t2 || $[21] !== t4 || $[22] !== t6 || $[23] !== t8) {
    t17 = [t2, t4, t6, t8, t10, t12, t14, t16];
    $[16] = t10;
    $[17] = t12;
    $[18] = t14;
    $[19] = t16;
    $[20] = t2;
    $[21] = t4;
    $[22] = t6;
    $[23] = t8;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  const metaTagValues = t17;
  let t18;
  if ($[25] !== metaTagValues) {
    t18 = metaTagValues.filter(_temp);
    $[25] = metaTagValues;
    $[26] = t18;
  } else {
    t18 = $[26];
  }
  const hasAnySeoMeta = t18.length > 0;
  const hasHtmlError = Boolean(data?.errors?.html);
  const hasRobotsData = Boolean(data?.robots?.fetched && ((data.robots.groups?.length ?? 0) > 0 || (data.robots.sitemaps?.length ?? 0) > 0));
  if (hasHtmlError && !hasRobotsData) {
    return null;
  }
  let t19;
  if ($[27] !== data || $[28] !== domain || $[29] !== hasAnySeoMeta || $[30] !== hasHtmlError || $[31] !== metaTagValues) {
    t19 = <SeoMetaBlock domain={domain} data={data} metaTagValues={metaTagValues} hasHtmlError={hasHtmlError} hasAnySeoMeta={hasAnySeoMeta} />;
    $[27] = data;
    $[28] = domain;
    $[29] = hasAnySeoMeta;
    $[30] = hasHtmlError;
    $[31] = metaTagValues;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  const t20 = data?.robots;
  let t21;
  if ($[33] !== domain || $[34] !== hasRobotsData || $[35] !== t20) {
    t21 = <RobotsBlock domain={domain} robots={t20} hasRobotsData={hasRobotsData} />;
    $[33] = domain;
    $[34] = hasRobotsData;
    $[35] = t20;
    $[36] = t21;
  } else {
    t21 = $[36];
  }
  let t22;
  if ($[37] !== t19 || $[38] !== t21) {
    t22 = <ReportSection {...sections.seo}><div className="space-y-4">{t19}{t21}</div></ReportSection>;
    $[37] = t19;
    $[38] = t21;
    $[39] = t22;
  } else {
    t22 = $[39];
  }
  return t22;
}
function _temp(t) {
  return t.value != null;
}